import { type AnimationItem, type AnimationConfigWithData } from 'lottie-web';
import { useRef, useEffect, memo, type CSSProperties } from 'react';

/**
 * A simple Lottie Wrapper for loading animations
 *
 * https://github.com/chenqingspring/react-lottie/issues/139
 */
type LottieProps = {
    options: Omit<AnimationConfigWithData, 'container'>;
    width?: string;
    height?: string;
    ariaRole?: string;
    ariaLabel?: string;
    speed?: number;
    style?: CSSProperties;
};

const Lottie = ({
    options,
    width = 'auto',
    height = 'auto',
    ariaRole = 'presentation',
    ariaLabel = undefined,
    speed = 1,
    style = {},
}: LottieProps) => {
    const elementRef = useRef<HTMLDivElement | null>(null);
    const lottieInstanceRef = useRef<AnimationItem | null>(null);

    useEffect(() => {
        (async () => {
            const { default: lottie } = await import('lottie-web');
            if (elementRef.current) {
                lottieInstanceRef.current = lottie.loadAnimation({
                    ...options,
                    container: elementRef.current,
                });
                if (speed) lottieInstanceRef.current.setSpeed(speed);
            }
        })();
        return () => {
            if (lottieInstanceRef.current) {
                lottieInstanceRef.current.destroy();
                lottieInstanceRef.current = null;
            }
        };
    }, [options, speed]);

    return (
        <div
            aria-label={ariaLabel}
            role={ariaRole}
            style={{
                width,
                height,
                overflow: 'hidden',
                margin: '0 auto',
                outline: 'none',
                ...style,
            }}
            ref={elementRef}
        />
    );
};

export default memo(Lottie);
